import { CheckOutlined, HomeOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { PaperPlane } from '@styled-icons/boxicons-regular/PaperPlane';
import { useLocation, useNavigate } from '@tanstack/react-location';
import { Button, Drawer, message, Popover } from 'antd';
import { UploadFile } from 'antd/lib';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnyAction } from 'redux';
import styled from 'styled-components';

import Box from '@/components/Box';
import { StyledIconButton } from '@/components/Buttons';
import ChatTextArea from '@/components/ChatTextArea';
import Message from '@/components/Message';
import SelectLeaseAgreements, {
  SelectedLeaseAgreement,
} from '@/components/SelectLeaseAgreements';
import { usePinnedConversationStore } from '@/components/UnansweredMessagesPopup/pinnedConversationStore';
import UploadToConversation from '@/components/UploadFile/UploadToConversation';
import {
  DocumentValuesFragment,
  GetConversationQuery,
  GetConversationsDocument,
  useAddMessageMutation,
  useArchiveConversationMutation,
  useUnansweredMessagesPopupLazyQuery,
} from '@/generated/graphql';
import useAppDispatch from '@/hooks/useAppDispatch';
import useAppSelector from '@/hooks/useAppSelector';
import useUser from '@/hooks/useUser';
import { updateMessageDraft } from '@/state/customer/actions';

interface Props {
  conversationId: string;
  answered: boolean;
  searchRequestReference?: GetConversationQuery['conversation']['searchRequestReference'];

  isMobile?: boolean;
}

const SendIcon = styled(PaperPlane)`
  margin-bottom: 5px;
  margin-right: 4px;
`;

const SendButton = styled(StyledIconButton).attrs({ $withText: true })`
  align-self: flex-end;
`;

const SelectLeaseAgreementsContainer = styled.div`
  width: min(600px, 100%);
  max-width: 100%;
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 100%;
  }
`;

const ChipButton = styled(Button)<{ $danger?: boolean }>`
  &&& {
    border-radius: 999px;
    padding-inline: 14px;
    height: auto;
    padding-block: 4px;
    border-color: ${({ theme, $danger }) =>
      $danger ? theme.colors.red : theme.colors.primaryPurple};
    color: ${({ theme, $danger }) =>
      $danger ? theme.colors.white : theme.colors.primaryPurple};
    background: ${({ theme, $danger }) =>
      $danger ? theme.colors.red : 'transparent'};

    &:hover,
    &:focus {
      border-color: ${({ theme, $danger }) =>
        $danger ? '#cf1322' : theme.colors.primaryPurpleDark};
      color: ${({ theme, $danger }) =>
        $danger ? theme.colors.white : theme.colors.primaryPurpleDark};
      background: ${({ theme, $danger }) =>
        $danger ? '#cf1322' : theme.colors.tableSelect};
    }
  }
`;

const ChatForm = ({
  conversationId,
  answered,
  searchRequestReference,
  isMobile = false,
}: Props): React.ReactElement => {
  const navigate = useNavigate();
  const path = useLocation();
  const dispatch = useAppDispatch();
  const [fileList, setFileList] = useState<
    UploadFile<DocumentValuesFragment>[]
  >([]);

  const apollo = useApolloClient();
  const [archiveConversation] = useArchiveConversationMutation({
    variables: {
      id: conversationId,
      archived: true,
    },
    onCompleted: async res => {
      res.setArchived.archived
        ? message.success(t('Conversation archived'))
        : message.success(t('Sent to inbox'));

      await apollo.reFetchObservableQueries();
    },
    onError: () => {
      message.error(t('error'));
    },
  });

  const latestDraft =
    useAppSelector(state => state.customer.messageDrafts[conversationId]) || '';
  const [selectLeaseAgreementsDrawerOpen, setSelectLeaseAgreementsDrawerOpen] =
    useState(false);
  const [selectedLeaseAgreements, setSelectedLeaseAgreements] = useState<
    SelectedLeaseAgreement[]
  >([]);

  const [uploadedAttachments, setUploadedAttachments] = useState<
    UploadFile<DocumentValuesFragment>[]
  >([]);

  const [addMessage, { error, loading }] = useAddMessageMutation({
    refetchQueries: [{ query: GetConversationsDocument }, 'GetConversation'],
  });
  const user = useUser();

  const [fetchUnanswered] = useUnansweredMessagesPopupLazyQuery({
    fetchPolicy: 'network-only',
  });

  const setPinnedConversationId = usePinnedConversationStore(
    state => state.setPinnedConversationId,
  );

  const peekNextUnansweredConversationId = async (): Promise<string | null> => {
    const isProposalsPage = path.current.pathname.startsWith('/s/');
    const referenceFilterId = isProposalsPage
      ? searchRequestReference?.id
      : undefined;
    try {
      const { data } = await fetchUnanswered({
        variables: { minAgeDays: 0, includeReplied: true },
      });
      const list = data?.unansweredMessagesPopup.conversations ?? [];
      const currentIndex = list.findIndex(
        c => c.conversationId === conversationId,
      );
      const startIndex = currentIndex >= 0 ? currentIndex + 1 : 0;
      const next = list.slice(startIndex).find(c => {
        if (c.conversationId === conversationId) {
          return false;
        }
        if (referenceFilterId) {
          return c.searchRequestReferenceId === referenceFilterId;
        }
        return true;
      });
      return next?.conversationId ?? null;
    } catch {
      return null;
    }
  };

  const goToConversationOrInbox = (nextConversationId: string | null) => {
    const inboxPath = path.current.pathname.replace(/\/\w+$/, '');
    // Pin only applies to the conversation the user opened from the popup.
    // Once they answer it, the next one in the queue follows the natural
    // inbox order without being pinned.
    setPinnedConversationId(null);
    if (nextConversationId) {
      navigate({ to: `${inboxPath}/${nextConversationId}` });
      return;
    }
    navigate({ to: inboxPath });
  };

  const { t } = useTranslation();

  const isLandlord =
    user?.activeClient?.type === 'landlord' && !user?.isPartner;
  const isTenant = user?.activeClient?.type === 'tenant';

  const updateMessage = (message: string): AnyAction =>
    dispatch(updateMessageDraft(conversationId, message));

  const messageDisabled =
    latestDraft === '' &&
    selectedLeaseAgreements.length === 0 &&
    uploadedAttachments.length === 0;

  const handleSubmit = async (): Promise<void> => {
    if (messageDisabled) {
      return;
    }
    const message = latestDraft;
    updateMessage('');
    const leaseAgreementIds = selectedLeaseAgreements.map(lease => lease.id);
    setSelectedLeaseAgreements([]);
    setUploadedAttachments([]);

    const isTurnDown = message.startsWith(t('turnDownMessage'));
    const shouldAutoSwitch = isTenant && !isTurnDown;
    // Peek before sending — once the message lands the current conversation
    // drops out of the unanswered list and we lose its position for the queue.
    const nextId = shouldAutoSwitch
      ? await peekNextUnansweredConversationId()
      : null;

    await addMessage({
      variables: {
        input: {
          conversationId,
          message,
          leaseAgreementIds,
          attachmentIds: uploadedAttachments.map(a => a.response.id),
        },
      },
    });

    if (isTurnDown) {
      await archiveConversation();
      navigate({
        to: path.current.pathname.replace(/\/\w+$/, ''),
      });
    } else if (shouldAutoSwitch) {
      goToConversationOrInbox(nextId);
    }
  };

  return (
    <Box
      w={'100%'}
      px={isMobile ? 0 : 3}
      py={isMobile ? 0 : 2}
      verticalSpacing={1}
      className={'chat-form-container'}
      style={{ boxSizing: 'border-box', maxWidth: '100%' }}
    >
      {error && <Message error={error} />}
      {searchRequestReference && !answered && isTenant && (
        <Box
          flex
          flexDirection="column"
          w={'100%'}
          style={{
            paddingLeft: 16,
            marginBottom: -4,
            boxSizing: 'border-box',
          }}
        >
          <Box
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
            }}
          >
            {(['moreInfo', 'followUp', 'pricing'] as const).map((key, i) => (
              <React.Fragment key={key}>
                <ChipButton
                  size="small"
                  onClick={() => updateMessage(t(`quickReplies.${key}`))}
                >
                  {t(`quickReplies.${key}`)}
                </ChipButton>
                {i === 1 && <div style={{ flexBasis: '100%', height: 0 }} />}
              </React.Fragment>
            ))}
            <ChipButton
              $danger
              size="small"
              onClick={async () => {
                if (!searchRequestReference?.id) {
                  return;
                }
                try {
                  const nextId = await peekNextUnansweredConversationId();
                  await addMessage({
                    variables: {
                      input: {
                        conversationId,
                        message: t('turnDownMessage'),
                        leaseAgreementIds: [],
                        attachmentIds: [],
                      },
                    },
                  });
                  await archiveConversation();
                  goToConversationOrInbox(nextId);
                } catch (error) {
                  console.error('Error declining:', error);
                }
              }}
              loading={loading}
            >
              {t('Tacka nej')}
            </ChipButton>
          </Box>
        </Box>
      )}
      <div>
        <ChatTextArea
          setMessage={message => updateMessage(message)}
          message={latestDraft}
          selectedLeaseAgreements={selectedLeaseAgreements}
          setSelectedLeaseAgreements={setSelectedLeaseAgreements}
          maxRows={6}
          minRows={3}
          selectedAttachments={uploadedAttachments}
          setSelectedAttachments={setUploadedAttachments}
          onChange={list => setUploadedAttachments(list)}
          fileList={fileList}
          setFileList={setFileList}
        />
      </div>

      <Box
        flex
        horizontalSpacing={1}
        justifyContent={'flex-end'}
        w={'100%'}
        style={{
          paddingRight: isMobile ? 12 : 14,
          boxSizing: 'border-box',
        }}
      >
        {(isLandlord || answered) && (
          <UploadToConversation
            values={uploadedAttachments}
            onChange={list => setUploadedAttachments(list)}
            fileList={fileList}
            setFileList={setFileList}
          />
        )}
        <Drawer
          open={selectLeaseAgreementsDrawerOpen}
          closable={false}
          height={'100%'}
          placement="bottom"
          footer={
            <div className="flex justify-end">
              <Button
                icon={<CheckOutlined />}
                onClick={() => setSelectLeaseAgreementsDrawerOpen(false)}
              >
                OK
              </Button>
            </div>
          }
        >
          <SelectLeaseAgreements
            searchRequestReference={searchRequestReference}
            setSelectedLeaseAgreements={setSelectedLeaseAgreements}
            selectedLeaseAgreements={selectedLeaseAgreements}
          />
        </Drawer>
        {isLandlord && (
          <Popover
            open={isMobile ? false : undefined}
            trigger={'click'}
            overlayClassName={'prevent-active-request-drawer-close'}
            content={
              <SelectLeaseAgreementsContainer
                className={'prevent-active-request-drawer-close h-[300px]'}
              >
                <SelectLeaseAgreements
                  searchRequestReference={searchRequestReference}
                  setSelectedLeaseAgreements={setSelectedLeaseAgreements}
                  selectedLeaseAgreements={selectedLeaseAgreements}
                />
              </SelectLeaseAgreementsContainer>
            }
            placement={'topRight'}
          >
            <Button
              icon={<HomeOutlined />}
              onClick={
                isMobile
                  ? () => setSelectLeaseAgreementsDrawerOpen(true)
                  : undefined
              }
            >
              {t('Suggest space')}{' '}
            </Button>
          </Popover>
        )}
        <SendButton
          type={'primary'}
          onClick={handleSubmit}
          loading={loading}
          icon={<SendIcon size={'17'} style={{ marginTop: '2px' }} />}
          disabled={messageDisabled}
        >
          {t('Send')}
        </SendButton>
      </Box>
    </Box>
  );
};

export default ChatForm;
